// admin-panel/src/pages/Quotes.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { quotesApi } from '../lib/api';
import { StatusBadge } from './Dashboard';
import { Plus, Search, Eye, Trash2, Send, MessageCircle, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Quotes() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ search: '', status: '', page: 1 });
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const { data, isLoading } = useQuery({
    queryKey: ['admin-quotes', filters],
    queryFn: () => quotesApi.list({ ...filters, per_page: 15 }),
  });

  const sendMut = useMutation({
    mutationFn: quotesApi.send,
    onSuccess: () => { qc.invalidateQueries(['admin-quotes']); toast.success('Quote marked as sent'); },
  });

  const waMut = useMutation({
    mutationFn: quotesApi.sendWhatsApp,
    onSuccess: (res) => window.open(res.wa_url, '_blank'),
  });

  const deleteMut = useMutation({
    mutationFn: quotesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-quotes']); toast.success('Deleted'); },
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Quotes</h1>
          <p className="text-sm text-[#555] mt-0.5">Manage customer quotes and estimates</p>
        </div>
        <Link to="/quotes/new" className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-all">
          <Plus size={16} /> New Quote
        </Link>
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Quotes', val: data.total, color: '#888' },
            { label: 'Pending', val: data.data?.filter(q => ['draft','sent'].includes(q.status)).length, color: '#D4AF37' },
            { label: 'Accepted', val: data.data?.filter(q => q.status === 'accepted').length, color: '#22c55e' },
            { label: 'Total Value', val: `₹${((data.total_value || 0)/1000).toFixed(0)}K`, color: '#D4AF37' },
          ].map(({ label, val, color }) => (
            <div key={label} className="bg-[#151515] border border-white/5 rounded-xl p-4">
              <div className="text-xl font-bold" style={{ color }}>{val ?? 0}</div>
              <div className="text-xs text-[#555] mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Search quotes…"
            className="w-full bg-[#151515] border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
        </div>
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
          className="bg-[#151515] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-[#888] focus:outline-none">
          {['','draft','sent','viewed','accepted','rejected','expired'].map(s => (
            <option key={s} value={s} className="bg-[#1A1A1A] capitalize">{s || 'All Status'}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {['Quote #','Client','Project Type','Amount','Status','Date','Actions'].map(h => (
                  <th key={h} className="text-left text-xs text-[#555] font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? Array(6).fill(0).map((_,i) => (
                <tr key={i} className="border-b border-white/5">
                  {Array(7).fill(0).map((_,j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-white/5 rounded animate-pulse w-20" /></td>)}
                </tr>
              )) : data?.data?.map(q => (
                <tr key={q.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-[#D4AF37] font-mono text-xs font-semibold">{q.quote_number}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{q.client_name}</div>
                    <div className="text-xs text-[#555]">{q.client_phone}</div>
                  </td>
                  <td className="px-4 py-3 text-[#888] text-xs">{q.project_type || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-white font-semibold">₹{Number(q.total_amount).toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-[#555] text-xs whitespace-nowrap">
                    {new Date(q.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link to={`/quotes/${q.id}/edit`} className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white transition-all"><Eye size={14} /></Link>
                      {q.status === 'draft' && (
                        <button onClick={() => sendMut.mutate(q.id)} className="p-1.5 rounded-lg hover:bg-blue-500/10 text-[#888] hover:text-blue-400 transition-all"><Send size={14} /></button>
                      )}
                      <button onClick={() => waMut.mutate(q.id)} className="p-1.5 rounded-lg hover:bg-green-500/10 text-[#888] hover:text-green-400 transition-all"><MessageCircle size={14} /></button>
<button
  onClick={() => {
    if (window.confirm('Delete?')) deleteMut.mutate(q.id)
  }}
  className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400 transition-all"
>
  <Trash2 size={14} />
</button>                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data?.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <div className="text-xs text-[#555]">Total: {data.total} quotes</div>
            <div className="flex gap-2">
              <button disabled={filters.page<=1} onClick={()=>setFilter('page',filters.page-1)} className="p-1.5 rounded-lg bg-white/5 disabled:opacity-30 text-[#888] hover:text-white transition-all"><ChevronLeft size={16}/></button>
              <span className="text-xs text-[#888] px-2 flex items-center">{filters.page}/{data.last_page}</span>
              <button disabled={filters.page>=data.last_page} onClick={()=>setFilter('page',filters.page+1)} className="p-1.5 rounded-lg bg-white/5 disabled:opacity-30 text-[#888] hover:text-white transition-all"><ChevronRight size={16}/></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
